import React, { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Film, LayoutGrid, Tag, Key, LogOut, Plus, Search, User as UserIcon, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import AddMovieDialog from "@/components/AddMovieDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function UserSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/users/search", { params: { q } });
        setResults(r.data);
        setOpen(true);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative w-64 hidden lg:block">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
      <Input
        value={q}
        onChange={e => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        placeholder="Find people…"
        data-testid="user-search-input"
        className="pl-9 h-9 bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-sm rounded-full" />
      {open && results.length > 0 && (
        <div className="absolute top-full mt-2 w-full bg-[#0F0F12] border border-white/10 rounded-lg overflow-hidden shadow-xl z-50" data-testid="user-search-results">
          {results.map(u => (
            <button
              key={u.username}
              onClick={() => { nav(`/u/${u.username}`); setQ(""); setOpen(false); }}
              data-testid={`user-search-result-${u.username}`}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/5 transition-colors text-left">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-[#1A1A1E] shrink-0">
                {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">{u.name?.[0]?.toUpperCase()}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{u.name}</div>
                <div className="text-xs text-slate-500 truncate">@{u.username}</div>
              </div>
              <Globe className="w-3.5 h-3.5 text-[#10B981]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  const nav = useNavigate();

  if (!user) return null;

  const links = [
    { to: "/dashboard", label: "Library", icon: LayoutGrid },
    { to: "/groups", label: "Groups", icon: Film },
    { to: "/tags", label: "Tags", icon: Tag },
    { to: "/settings", label: "MCP & API", icon: Key },
  ];

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 glass">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center gap-6">
          <button onClick={() => nav("/dashboard")} className="flex items-center gap-2 font-display text-lg font-semibold shrink-0" data-testid="brand-logo">
            <Film className="w-5 h-5 text-[#E11D48]" />
            <span>REEL<span className="text-[#E11D48]">KEEPER</span></span>
          </button>
          <nav className="hidden md:flex items-center gap-1">
            {links.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm flex items-center gap-2 transition-all ${
                    isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <l.icon className="w-4 h-4" /> {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex-1" />
          <UserSearch />
          <Button
            onClick={() => setAddOpen(true)}
            data-testid="add-movie-btn"
            className="bg-[#E11D48] hover:bg-rose-700 rounded-full h-9 px-4 text-sm"
          >
            <Plus className="w-4 h-4 mr-1" /> Add movie
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-9 h-9 rounded-full overflow-hidden border border-white/10" data-testid="user-menu-trigger">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#1A1A1E] flex items-center justify-center text-xs">{user.name?.[0]?.toUpperCase()}</div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0F0F12] border-white/10 text-slate-200 w-56">
              <DropdownMenuLabel className="text-xs text-slate-400">{user.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              {user.username && (
                <DropdownMenuItem onClick={() => nav(`/u/${user.username}`)} data-testid="menu-public-profile" className="cursor-pointer">
                  <UserIcon className="w-4 h-4 mr-2" /> View public profile
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => nav("/settings")} data-testid="menu-settings" className="cursor-pointer">
                <Key className="w-4 h-4 mr-2" /> Profile, MCP & API
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="cursor-pointer text-rose-400 focus:text-rose-300">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8 fade-up">
        {children ?? <Outlet context={{ openAdd: () => setAddOpen(true) }} />}
      </main>

      <AddMovieDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
